import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer/drawer';
import useMobile from '@/hook/useMobile';
import { useEffect, useState } from 'react';
import { X } from 'react-feather';
import { createPortal } from 'react-dom';

const DrawerBox = ({
  open,
  onOpenChange,
  closeOnClick,
  contentStyle,
  children,
  triggerElement,
  headerText,
  headerStyle,
  hasHeader = true,
  headerIcon,
  type = 'default',
  usePortal = false,
  shouldScaleBackground = true,
  repositionInputs = true,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  closeOnClick?: () => void;
  contentStyle?: string;
  hasHeader?: boolean;
  children: React.ReactNode;
  triggerDisabled?: boolean;
  triggerButtonText?: string;
  triggerButtonIcon?: React.ReactNode;
  triggerButtonLoading?: boolean;
  triggerButtonStyle?: string;
  triggerElement?: React.ReactNode;
  triggerButtonAction?: () => void;
  headerText?: React.ReactNode;
  headerStyle?: string;
  headerIcon?: React.ReactNode;
  type?: string;
  usePortal?: boolean;
  shouldScaleBackground?: boolean;
  repositionInputs?: boolean;
}) => {
  const isMobile = useMobile();
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (usePortal) {
      const portalRoot = document.getElementById('drawer-portal');
      if (portalRoot) {
        setPortalContainer(portalRoot);
      } else {
        const div = document.createElement('div');
        div.id = 'drawer-portal';
        document.body.appendChild(div);
        setPortalContainer(div);
      }
    }
  }, [usePortal]);

  const drawerBody = (
    <Drawer
      open={open && isMobile}
      onOpenChange={onOpenChange}
      onClose={closeOnClick}
      shouldScaleBackground={shouldScaleBackground}
      repositionInputs={repositionInputs}
    >
      {!usePortal && triggerElement && <DrawerTrigger asChild>{triggerElement}</DrawerTrigger>}
      <DrawerContent className={`${contentStyle} p-5 min-h-auto`}>
        {hasHeader && (
          <DrawerHeader>
            <div className='rounded-tl-xl rounded-tr-xl border-b border-border justify-between items-center flex pb-[20px]'>
              <div className='flex justify-start items-center gap-2'>
                <div className='justify-end items-center flex'>
                  <div className={`text-text flex items-center`}>{headerIcon}</div>
                </div>
                <DrawerTitle className={`${headerStyle} ${type === 'error' ? 'text-red-1' : ''}`}>
                  {headerText}
                </DrawerTitle>
              </div>
              <div className='flex rounded-md cursor-pointer' onClick={closeOnClick}>
                <DrawerClose className='text-text focus-visible:outline-none'>
                  <X className='text-text' size={24} strokeWidth={'1'} />
                </DrawerClose>
              </div>
            </div>
          </DrawerHeader>
        )}
        {children}
      </DrawerContent>
    </Drawer>
  );

  if (!isMobile) return null;

  return usePortal && portalContainer ? createPortal(drawerBody, portalContainer) : drawerBody;
};

export default DrawerBox;
